export const DEFAULT_FOLDER_ID = 'home';

export const SPACE_FOLDER_IDS_PARAM = 'folderIds';
export const SPACE_FOLDER_NAMES_PARAM = 'folderNames';

export type SpaceFolderTrail = {
  folderId: string;
  folderIds: string[];
  folderNames: string[];
};

const SPACE_PATHNAME = '/bricks';

const ROOT_FOLDER_ID_PREFIX = 'rootFolderId';
const ROOT_FOLDER_TITLE = 'Home';

const joinTrail = (values: string[]) => values.join('/');

/** The API root folder is the Workspace itself, so it never belongs in the trail. */
export const isRootFolder = (folder: { id?: string; title?: string }): boolean => {
  const id = folder.id ?? '';
  return (
    !id ||
    id === DEFAULT_FOLDER_ID ||
    id.startsWith(ROOT_FOLDER_ID_PREFIX) ||
    folder.title === ROOT_FOLDER_TITLE
  );
};

/** Keeps ids and names aligned while dropping the root folder from both. */
const zipTrail = (
  folderIds: string[],
  folderNames: string[],
): Array<{ id: string; title: string }> => {
  const length = Math.min(folderIds.length, folderNames.length);
  return folderIds
    .slice(0, length)
    .map((id, index) => ({ id, title: folderNames[index] }))
    .filter((folder) => !isRootFolder(folder));
};

/** Serializes a folder trail into the `folderIds`/`folderNames` query values. */
export const serializeFolderTrailParams = (
  folderIds: string[] = [],
  folderNames: string[] = [],
): { folderIds: string; folderNames: string } | null => {
  const trail = zipTrail(folderIds, folderNames);
  if (trail.length === 0) {
    return null;
  }
  return {
    folderIds: joinTrail(trail.map((folder) => folder.id)),
    folderNames: joinTrail(trail.map((folder) => folder.title)),
  };
};

export const parseTrailParam = (raw: string | string[] | undefined): string[] => {
  if (raw === undefined || raw === null) return [];
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value) return [];
  return value
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
};

/**
 * Builds a Workspace URL with menu/level always set.
 * Avoids PathBuilder double-`?` when base path already contains query params.
 */
export function buildSpaceUrl(options?: {
  folderId?: string | null;
  folderIds?: string[];
  folderNames?: string[];
}): string {
  const params = new URLSearchParams();
  params.set('menu', 'space');
  params.set('level', '1');

  const folderId = options?.folderId;
  if (folderId && !isRootFolder({ id: folderId })) {
    params.set('folder', folderId);
  }

  const trail = zipTrail(options?.folderIds ?? [], options?.folderNames ?? []);

  if (trail.length > 0) {
    params.set(SPACE_FOLDER_IDS_PARAM, joinTrail(trail.map((folder) => folder.id)));
    params.set(SPACE_FOLDER_NAMES_PARAM, joinTrail(trail.map((folder) => folder.title)));
  }

  return `${SPACE_PATHNAME}?${params.toString()}`;
}

export function buildSpaceFolderTrail(
  folders: Array<{ id: string; title: string }>,
): SpaceFolderTrail | null {
  const nested = folders.filter((folder) => !isRootFolder(folder));
  if (nested.length === 0) {
    return null;
  }
  const last = nested[nested.length - 1];
  return {
    folderId: last.id,
    folderIds: nested.map((folder) => folder.id),
    folderNames: nested.map((folder) => folder.title),
  };
}

/**
 * When navigating to `targetFolderId` from the current trail:
 * - if target is an ancestor, truncate the trail
 * - if target is a new child, append it (title required for names)
 */
export function resolveSpaceFolderTrail(params: {
  currentTrail: Array<{ id: string; title: string }>;
  targetFolderId: string;
  targetFolderTitle?: string;
}): SpaceFolderTrail | null {
  const { currentTrail, targetFolderId, targetFolderTitle } = params;

  if (!targetFolderId || isRootFolder({ id: targetFolderId, title: targetFolderTitle })) {
    return null;
  }

  const withoutHome = currentTrail.filter((folder) => !isRootFolder(folder));
  const ancestorIndex = withoutHome.findIndex((folder) => folder.id === targetFolderId);

  if (ancestorIndex >= 0) {
    return buildSpaceFolderTrail(withoutHome.slice(0, ancestorIndex + 1));
  }

  const nextTitle = targetFolderTitle || targetFolderId;
  return buildSpaceFolderTrail([...withoutHome, { id: targetFolderId, title: nextTitle }]);
}

export function readSpaceFolderTrailFromQuery(query: {
  folder?: string | string[];
  folderIds?: string | string[];
  folderNames?: string | string[];
}): SpaceFolderTrail | null {
  const folderIds = parseTrailParam(query[SPACE_FOLDER_IDS_PARAM] ?? query.folderIds);
  const folderNames = parseTrailParam(query[SPACE_FOLDER_NAMES_PARAM] ?? query.folderNames);
  const folderRaw = query.folder;
  const folderId = Array.isArray(folderRaw) ? folderRaw[0] : folderRaw;

  const trail = zipTrail(folderIds, folderNames);
  if (trail.length > 0) {
    return buildSpaceFolderTrail(trail);
  }

  if (folderId && !isRootFolder({ id: folderId })) {
    return {
      folderId,
      folderIds: [folderId],
      folderNames: [],
    };
  }

  return null;
}
